// The header's Stage field: which stage of the project the page shows, each
// with its report; drawn like the real project card's analysis widget.

import { useEffect, useRef, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import { StatusDot, StatusDotColor } from "../../../../design-system";
import classes from "./AnalysisPicker.module.less";

export interface AnalysisItem<K extends string> {
    key: K;
    label: string;
    /** Under the label in the list: the date, or the current status for the latest stage. */
    date: string;
    color: StatusDotColor;
    latest?: boolean;
}

export const AnalysisPicker = <K extends string>({
    items,
    value,
    onChange,
}: {
    items: AnalysisItem<K>[];
    value: K;
    onChange: (key: K) => void;
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);
    const selected = items.find((i) => i.key === value) ?? items[0];
    return (
        <div className={classes.wrapper} ref={ref}>
            <button type="button" className={classes.trigger} onClick={() => setOpen((o) => !o)}>
                <StatusDot color={selected.color}>{selected.label}</StatusDot>
                <DownOutlined className={classes.chevron} />
            </button>
            {open && (
                <div className={classes.dropdown}>
                    <div className={classes.dropdownTitle}>Stages</div>
                    {[...items].reverse().map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={clsx(
                                classes.item,
                                item.key === selected.key && classes.itemSelected
                            )}
                            onClick={() => {
                                onChange(item.key);
                                setOpen(false);
                            }}
                        >
                            <div>
                                <div className={classes.itemLabel}>
                                    <StatusDot color={item.color}>{item.label}</StatusDot>
                                </div>
                                <div className={classes.itemDate}>{item.date}</div>
                            </div>
                            {item.latest && <span className={classes.tag}>Latest</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
